import mapboxgl from "mapbox-gl";
import Route from "../../../../../common/Route";
import * as turf from "@turf/turf";

export default class NavMapRenderer {


  constructor(core, container, center = null, darkMode = false) {

    this._core = core;
    this._container = container;
    this.settings = {};
    this.styleUrl = this.settings.styleUrl || "mapbox://styles/mapbox/standard";
    this.styleId = this.settings.styleId || "standard";
    this._darkMode = darkMode;

    this._routeGeometry = null;
    this._bearing = 0;

    this._map = new mapboxgl.Map({
      container,
      center,
      style: this.styleUrl,
      zoom: 9,
      attributionControl: false,
      logoPosition: window.innerWidth > 416 ? "bottom-right" : "bottom",
      maxZoom: 100,
      interactive: true
    });

    this._lineColorDriven = "rgba(0,0,0,0.25)";
    this._lineColorRoute = "#000";

    this._lineColorCongestion = "#FF6600";

    this._centered = false;

    this._ro = new ResizeObserver(() => {
      if (!this._destroyed) {
        requestAnimationFrame(() => {
          try {
            this._map?.resize();
          } catch { }
        });
      }
    });

    this._ro.observe(this._container);
    this._destroyed = false;

    this._markerElement = this.createArrowMarker(0);

    this._marker = new mapboxgl.Marker({
      element: this._markerElement
    });

    this._suggestionMarker = new mapboxgl.Marker({
      color: "#000"
    });

    this._map.on("load", () => this.setup());
  }

  async setup() {

    if (this._destroyed) return;

    this._map.setConfigProperty("basemap", "lightPreset", this._darkMode ? "night" : "day");
    this._map.setConfigProperty("basemap", "showPointofInterestLabels", false);
    this._map.setConfigProperty("basemap", "showPlaceLabels", false);

    this._map.addSource("routing-data", {
      type: "geojson",
      data: Route.EmptyGeoJson(),
      lineMetrics: true
    });

    this._map.addSource("congestion-data", {
      type: "geojson",
      data: Route.EmptyGeoJson(),
      lineMetrics: true
    });


    this._map.addLayer({
      type: "line",
      source: "routing-data",
      id: "routing-layer",
      slot: "middle",
      paint: {
        "line-width": 12,
        "line-opacity": 1,
        "line-color": "#ff0000",
        'line-gradient': ['interpolate', ['linear'], ['line-progress'], 0, this._lineColorDriven, 1, this._lineColorDriven]
      },
      layout: {
        "line-cap": "round"
      }
    });


    this._map.addLayer({
      type: "line",
      source: "congestion-data",
      id: "congestion-layer",
      slot: "middle",
      paint: {
        "line-width": 3.5,
        "line-opacity": 0.9,
        "line-color": this._lineColorCongestion,
        "line-dasharray": [4, 3]
      },
      layout: {
        "line-cap": "round"
      }
    });



    this._core.geolocation.on("location", (pos, progressKm, bearing) => this.onLocation(pos, progressKm, bearing));

    const deCenterFn = () => {
      if (this._centered) {
        this._centered = false;
        this._core.emit("centered", false);
        this._markerElement.innerHTML = this.createMarkerSVG(this._centered);
      }
    };

    this._map.on("touchstart", deCenterFn);
    this._map.on("mousedown", deCenterFn);


    this._core.setReady();

    const initPos = await this._core.getLocation();

    if (!!initPos && !this.hasRoute()) {
      this._map.flyTo({
        center: [initPos.coords.longitude, initPos.coords.latitude],
        zoom: 11,
        padding: 64
      });
    }

  }

  get mapbox() {
    return this._map;
  }

  createArrowMarker() {
    const el = document.createElement('div');
    el.className = 'arrow-marker';

    el.style = `width: 120px; height: 120px; display: flex; align-items: center; justify-content: center;`;
    el.innerHTML = this.createMarkerSVG(this._centered);

    return el;
  }

  createMarkerSVG(centered) {
    if (centered) {
      return (`
        <svg viewBox="0 0 34 34" style="width: 100px; height: 100px; transform: rotateX(60deg) !important; filter: drop-shadow( 0px 3px 2px rgba(0, 0, 0, .5))">
          <circle r="17" cx="17" cy="17" fill="black" />
          <circle r="15" cx="17" cy="17" fill="white" />
          <polygon points="17,4 27,27 17,22 7,27" fill="black"/>
        </svg>
    `);
    } else {
      return (`
        <svg viewBox="0 0 34 34" style="width: 56px; height: 56px; !important; filter: drop-shadow( 0px 3px 2px rgba(0, 0, 0, .5))">
          <circle r="17" cx="17" cy="17" fill="black" />
          <circle r="16" cx="17" cy="17" fill="white" />
          <circle r="8" cx="17" cy="17" fill="black" />
        </svg>
    `);
    }
  }

  setSuggestion(coords) {
    this._suggestionMarker?.setLngLat(coords).addTo(this._map);
  }

  removeSuggestion() {
    this._suggestionMarker.remove();
  }

  setRoute(route) {
    this._route = route;
    this._routeGeometry = route.geometry;

    this._map.getSource("routing-data")?.setData({
      "type": "Feature",
      "properties": {},
      "geometry": this._routeGeometry
    });

    this._map.getSource("congestion-data")?.setData(route.congestionGeometry);
  }

  cancel() {
    this._route = null;
    this._routeGeometry = null;
    this._map.getSource("routing-data")?.setData({
      "type": "FeatureCollection",
      "features": []
    });

    this._map.getSource("congestion-data")?.setData({
      "type": "FeatureCollection",
      "features": []
    });
  }

  hasRoute() {
    return !!this._routeGeometry;
  }

  onLocation(pos, progressKm, bearing) {

    this._marker?.setLngLat([pos.coords.longitude, pos.coords.latitude]).addTo(this._map);

    if (this.hasRoute()) {

      // Paint route
      const progress = Math.max(0.000011, Math.min(progressKm / this._route.lengthKm, 0.99998));
      
      // Grad Route
      const grad = [
        "interpolate", ["linear"], ["line-progress"],
        0, this._lineColorDriven,
        progress - 0.00001, this._lineColorDriven,    // up to p ⇒ red
        progress + 0.00001, this._lineColorRoute,    // at  p ⇒ jump to blue
        1, this._lineColorRoute
      ];

      this._map.setPaintProperty("routing-layer", "line-gradient", grad);
    }

    // animate bearing
    const delta = ((bearing - this._bearing + 540) % 360) - 180;
    const current = this._bearing + delta * (this.hasRoute() ? 0.2 : 1);
    this._bearing = (current % 360 + 360) % 360;

    const cameraPosition = turf.destination(
      turf.point([pos.coords.longitude, pos.coords.latitude]),
      0.175,
      this._bearing + 180
    );

    if (this._centered) {

      const camera = this._map.getFreeCameraOptions();

      camera.position = mapboxgl.MercatorCoordinate.fromLngLat(
        {
          lng: cameraPosition.geometry.coordinates[0],
          lat: cameraPosition.geometry.coordinates[1],
        },
        120
      );

      const cameraLookAtPosition = turf.destination(
        turf.point([pos.coords.longitude, pos.coords.latitude]),
        0.1,
        this._bearing
      );

      // tell the camera to look at a point along the route
      camera.lookAtPoint({
        lng: cameraLookAtPosition.geometry.coordinates[0],
        lat: cameraLookAtPosition.geometry.coordinates[1],
      });

      this._map.setFreeCameraOptions(camera);
    }
  }

  recenter() {
    this._centered = true;
    this._core.emit("centered", true);
    this._markerElement.innerHTML = this.createMarkerSVG(this._centered);
  }


  destroy() {
    this._destroyed = true;
    this._map?.remove();
    this._ro?.disconnect();
  }

}